/**
 * Audible and haptic confirmation for the handheld.
 *
 * The scanner's own decode beep only says "I read a barcode". It says nothing
 * about whether Prism understood it, which is the part the operator actually
 * needs while facing away from the screen with both hands full. These tones
 * report the outcome instead:
 *
 *   ok       a scan was understood and accepted
 *   done     a putaway or move was committed
 *   problem  the scan was rejected, or the commit failed
 *
 * Tones are generated rather than loaded so there is no asset to fetch, and
 * every call is wrapped: a warehouse tool must never fail because audio is
 * unavailable, blocked, or the device is muted.
 *
 * The catch is browser autoplay policy. An AudioContext starts suspended and
 * only resumes inside a real user gesture, and a DataWedge scan does not count
 * as one: the keystrokes are injected by the OS, so the page never gets user
 * activation and every tone is silently dropped.
 *
 * Nobody should have to be told that, so this module arms itself. It listens
 * for the first real interaction anywhere in the app and wakes the audio
 * pipeline then. Signing in is a tap, and activation is sticky for the life of
 * the document, so by the time an operator reaches a handheld screen the sound
 * is already live without anyone opting in.
 */

// Frequencies in Hz, durations in seconds. `done` rises so it is distinct from
// `ok` without being longer, and `problem` sits well below both so it is
// unmistakable over machinery.
// Loud and long enough to carry over a warehouse from a phone speaker. The
// first attempt was 60ms at 0.08 gain, which is inaudible more than a foot
// away. Small speakers are most efficient around 2-4kHz, so the confirmations
// sit up there; only the rejection drops low, where it cannot be mistaken for
// a success.
// Raised twice after listening on the device: 0.08 was inaudible, 0.35 was
// still lost against the room. A square wave is already harmonically rich, so
// this is loud and deliberately a bit harsh — which is what carries.
const PEAK_GAIN = 0.7

const TONES = {
    ok: [{ hz: 2600, seconds: 0.11 }],
    done: [{ hz: 2000, seconds: 0.1 }, { hz: 3000, seconds: 0.14 }],
    problem: [{ hz: 420, seconds: 0.18 }, { hz: 300, seconds: 0.24 }]
}

const VIBRATION = {
    ok: 25,
    done: [30, 40, 30],
    problem: [90, 60, 90]
}

let audioContext = null

/**
 * Wake the audio pipeline. MUST be called from inside a real user gesture
 * handler (a tap), or the browser leaves the context suspended.
 * @returns {boolean} whether audio is now running.
 */
export function primeFeedback() {
    try {
        const context = getContext()
        if (!context) return false
        if (context.state === 'suspended') context.resume()
        return context.state !== 'suspended'
    } catch {
        return false
    }
}

/** True once tones will actually be audible. */
export function isFeedbackReady() {
    try {
        return Boolean(audioContext) && audioContext.state === 'running'
    } catch {
        return false
    }
}

/** Sound and buzz the outcome of a scan. Never throws. */
export function signal(kind) {
    const tones = TONES[kind]
    if (!tones) return
    playTones(tones)
    buzz(VIBRATION[kind])
}

function playTones(tones) {
    try {
        const context = getContext()
        if (!context) return
        // Autoplay policy suspends the context until a gesture. A scan is a
        // key event, which counts, but resuming is cheap and idempotent.
        if (context.state === 'suspended') context.resume()

        let startAt = context.currentTime
        for (const { hz, seconds } of tones) {
            const oscillator = context.createOscillator()
            const gain = context.createGain()
            oscillator.type = 'square'
            oscillator.frequency.value = hz
            // Ramp the tail so the tone stops without an audible click.
            gain.gain.setValueAtTime(PEAK_GAIN, startAt)
            gain.gain.exponentialRampToValueAtTime(0.0001, startAt + seconds)
            oscillator.connect(gain).connect(context.destination)
            oscillator.start(startAt)
            oscillator.stop(startAt + seconds)
            startAt += seconds
        }
    } catch {
        // Muted, blocked or unsupported. The screen still says what happened.
    }
}

function buzz(pattern) {
    try {
        if (pattern && typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(pattern)
        }
    } catch {
        // Vibration is a bonus, never a requirement.
    }
}

/**
 * Wake the audio pipeline on the first real interaction anywhere in the app.
 *
 * Capture phase and `once`, so it costs one listener for the life of the tab
 * and cannot be swallowed by a handler that stops propagation. `keydown` is
 * included for a physical keyboard; a scanner's synthetic keystrokes will not
 * satisfy the browser, but a real key press will.
 */
function armOnFirstGesture() {
    if (typeof document === 'undefined') return
    const wake = () => { primeFeedback() }
    for (const event of ['pointerdown', 'touchend', 'keydown']) {
        document.addEventListener(event, wake, { capture: true, once: true, passive: true })
    }
}

armOnFirstGesture()

function getContext() {
    if (audioContext) return audioContext
    const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
    if (!Ctor) return null
    audioContext = new Ctor()
    return audioContext
}
